import React, {useEffect, FocusEvent, useCallback} from 'react';
import {TextInput, SelectInput, Text, Button, Modal, PhoneInput} from '@/ui-kits';
import {SelectInputitem, AddAdminRequestPayload} from '@/interface';
import {useFormik} from 'formik';
import {AddAdminFormValidation} from '@/utils/schema';
import {createFormattedBlurHandler} from '@/utils/common_functions';

interface AddAdminModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (formData: AddAdminRequestPayload) => void;
  roleOptions: SelectInputitem[];
  statusOptions: SelectInputitem[];
}

export const AddAdminModal: React.FC<AddAdminModalProps> = ({open, onClose, onSave, roleOptions, statusOptions}) => {
  const {values, errors, touched, handleBlur, handleSubmit, resetForm, setFieldValue, isValid, dirty} = useFormik<AddAdminRequestPayload>({
    initialValues: {
      name: '',
      email: '',
      phone: '',
      role: 0,
      status: true,
    },
    validationSchema: AddAdminFormValidation,
    onSubmit: formValues => {
      onSave(formValues);
    },
    validateOnChange: true,
    validateOnBlur: true,
  });

  // Check if all required fields are filled
  const isFormComplete = values.name.trim() !== '' && values.email.trim() !== '' && values.phone.trim() !== '' && values.role !== 0 && values.status !== null;

  // Disable save button if form is not valid or not complete
  const isSaveDisabled = !isValid || !isFormComplete;

  const handleNameBlur = createFormattedBlurHandler(handleBlur, setFieldValue, 'name');

  const closeModal = useCallback(() => {
    if (!dirty) {
      onClose();
    }
  }, [dirty]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open, resetForm]);

  return (
    <Modal open={open} onClose={closeModal} className="relative bg-white py-6 px-8 rounded-xl gap-6 box-border w-[92vw] sm:w-auto max-w-[520px] overflow-y-auto">
      <Text variant="free" className="text-ui_blue font-FigtreeBold mb-6 text-3xl">
        Add New Admin
      </Text>

      <div className="flex flex-col gap-4">
        {/* Full Name */}
        <TextInput
          name="name"
          label="Full Name"
          value={values.name}
          onChangeText={text => setFieldValue('name', text)}
          onBlur={handleNameBlur as any}
          placeholder="Enter full name"
          wrapperClassName="border w-full"
          error={errors.name}
          touched={touched.name}
          required
        />

        {/* Email Address */}
        <TextInput
          name="email"
          label="Email Address"
          value={values.email}
          onChangeText={text => setFieldValue('email', text)}
          onBlur={handleBlur}
          placeholder="Enter email address"
          wrapperClassName="border w-full"
          error={errors.email}
          touched={touched.email}
          required
        />

        {/* Phone Number */}
        <PhoneInput
          name="phone"
          label="Phone Number"
          value={values.phone}
          onChange={(phone) => setFieldValue('phone', phone)}
          onBlur={handleBlur('phone')}
          placeholder="Enter phone number"
          wrapperClassName="border w-full"
          error={errors.phone}
          touched={touched.phone}
          required
        />

        {/* Role */}
        <SelectInput
          wrapperClassName="border w-full"
          name="role"
          required
          label="Role"
          placeholder="Select Role"
          options={roleOptions}
          value={values.role}
          onChange={item => setFieldValue('role', item.id)}
          itemTextClassName="text-sm"
          dropdownPosition='above'
        />

        {/* Status */}
        <SelectInput
          wrapperClassName="border-[1px] w-full"
          name="status"
          label="Status"
          required
          placeholder="Select Status"
          options={statusOptions}
          value={values.status ? 1 : 2}
          onChange={item => setFieldValue('status', item.id === 1)}
          itemTextClassName="text-sm"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row-reverse gap-4 w-full mt-6">
        <Button
          textClassName="text-sm"
          onClick={() => handleSubmit()}
          variant="primary"
          title="Save"
          className="w-full grow px-8 py-2"
          disabled={isSaveDisabled}
        />
        <Button textClassName="text-sm" onClick={onClose} variant="secondary" title="Cancel" className="w-full grow px-8 py-2 border-[1px]" />
      </div>
    </Modal>
  );
};
